import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import type { Message, SupportArticlePreview, SupportTicketCreateResult } from '@widget/types'

// Dashboard-specific storage key (tenant-scoped). Deliberately different from
// the public widget's `r3c_session_*` keys so the internal support chat never
// shares state with embedded widgets on other sites.
const STORAGE_KEY = 'r3c_dashboard_support_chat'

// The internal support chat always talks to the r3Converse platform support
// tenant, not to the logged-in tenant's own support add-on. This makes it
// available to all dashboard users regardless of their plan or add-ons.
export const PLATFORM_SUPPORT_TENANT_ID = '00000000-0000-0000-0000-000000000001'

interface StoredSession {
  messages: Message[]
  conversationId?: string
  unreadCount: number
}

interface SupportChatState {
  messages: Message[]
  isOpen: boolean
  isTyping: boolean
  unreadCount: number
  conversationId?: string
  suggestedArticles?: SupportArticlePreview[]
}

function getStoredSession(tenantId: string): StoredSession | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${tenantId}`)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function saveSession(tenantId: string, session: StoredSession) {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${tenantId}`, JSON.stringify(session))
  } catch {
    // Ignore storage errors
  }
}

/**
 * Internal support chat for the dashboard. Talks to the platform support
 * tenant (r3Converse's own support bot) through the public support endpoints,
 * so every authenticated user gets help regardless of their plan or add-ons.
 * The logged-in user id identifies the chatter via identify().
 */
export function useSupportChat() {
  const { t, i18n } = useTranslation()
  const userId = useAuthStore((s) => s.user?.id)
  const userEmail = useAuthStore((s) => s.user?.email)
  const userName = useAuthStore((s) => s.user?.name)

  const [state, setState] = useState<SupportChatState>(() => {
    const stored = getStoredSession(PLATFORM_SUPPORT_TENANT_ID)
    return {
      messages: stored?.messages || [],
      isOpen: false,
      isTyping: false,
      unreadCount: stored?.unreadCount || 0,
      conversationId: stored?.conversationId,
      suggestedArticles: undefined,
    }
  })

  // Keep the latest messages in a ref so sendMessage can build the history
  // without depending on state in its closure.
  const messagesRef = useRef<Message[]>(state.messages)
  useEffect(() => {
    messagesRef.current = state.messages
  }, [state.messages])

  const persist = useCallback(
    (next: SupportChatState) => {
      saveSession(PLATFORM_SUPPORT_TENANT_ID, {
        messages: next.messages,
        conversationId: next.conversationId,
        unreadCount: next.unreadCount,
      })
    },
    []
  )

  const sendMessage = useCallback(
    async (content: string) => {
      if (!userId) return

      const userMessage: Message = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      }

      setState((prev) => {
        const next = {
          ...prev,
          messages: [...prev.messages, userMessage],
          isTyping: true,
        }
        persist(next)
        return next
      })

      try {
        // Send conversation history (last 19 messages + the new one) so the
        // AI has full context, same contract as the public widget.
        const history = [...messagesRef.current.slice(-19), userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        }))

        const response = await fetch(
          `https://api.r3converse.com/api/v1/support/chat/${PLATFORM_SUPPORT_TENANT_ID}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              messages: history,
              locale: i18n.language || 'en',
              visitorId: userId,
              conversationId: state.conversationId,
            }),
          }
        )

        if (!response.ok) throw new Error(`Support chat failed: ${response.status}`)
        const data = (await response.json())?.data

        const aiMessage: Message = {
          id: `msg_${Date.now()}_ai`,
          role: 'assistant',
          content: data?.response || t('errors.processMessage'),
          timestamp: new Date().toISOString(),
        }

        setState((prev) => {
          const next = {
            ...prev,
            messages: [...prev.messages, aiMessage],
            isTyping: false,
            unreadCount: prev.isOpen ? 0 : prev.unreadCount + 1,
            conversationId: data?.conversationId || prev.conversationId,
            suggestedArticles: data?.suggestedArticles,
          }
          persist(next)
          return next
        })
      } catch (error: any) {
        console.error('[SupportChat] Chat error:', error.message)

        const errorMessage: Message = {
          id: `msg_${Date.now()}_error`,
          role: 'assistant',
          content: t('errors.generic'),
          timestamp: new Date().toISOString(),
        }
        setState((prev) => {
          const next = { ...prev, messages: [...prev.messages, errorMessage], isTyping: false }
          persist(next)
          return next
        })
      }
    },
    [userId, state.conversationId, i18n.language, t, persist]
  )

  const createTicket = useCallback(
    async (data: { subject: string; description: string; email?: string; name?: string }) => {
      const response = await fetch(
        `https://api.r3converse.com/api/v1/support/tickets/${PLATFORM_SUPPORT_TENANT_ID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...data,
            email: data.email || userEmail,
            name: data.name || userName,
            conversationId: state.conversationId,
          }),
        }
      )
      if (!response.ok) throw new Error(`Ticket creation failed: ${response.status}`)
      return (await response.json())?.data as SupportTicketCreateResult
    },
    [state.conversationId, userEmail, userName]
  )

  const toggleOpen = useCallback(() => {
    setState((prev) => {
      const next = { ...prev, isOpen: !prev.isOpen, unreadCount: 0 }
      persist(next)
      return next
    })
  }, [persist])

  const setOpen = useCallback(
    (open: boolean) => {
      setState((prev) => {
        const next = { ...prev, isOpen: open, unreadCount: open ? 0 : prev.unreadCount }
        persist(next)
        return next
      })
    },
    [persist]
  )

  const clearConversation = useCallback(() => {
    setState((prev) => {
      const next = {
        ...prev,
        messages: [],
        conversationId: undefined,
        suggestedArticles: undefined,
      }
      persist(next)
      return next
    })
  }, [persist])

  return {
    ...state,
    sendMessage,
    createTicket,
    toggleOpen,
    setOpen,
    clearConversation,
  }
}
